import {FC, useMemo, useState} from 'react';
import {useCan, usePersona} from 'src/permissions/hooks';
import {Box} from '@mui/material';
import {useSearchParams} from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {useAppSelector, useBusinessId} from 'src/app/hooks';
import {reportServiceApi} from 'src/api';
import {ConsumptionRequest} from 'src/types';
import {FiltersBar, EnergyTypeFilter} from './components/FiltersBar';
import {KpiGrid} from './components/KpiGrid';
import {TimeSeriesChart, Granularity} from './components/TimeSeriesChart';
import {BreakdownTabKey, BreakdownTabs} from './components/BreakdownTabs';
import {energyConsumptionSx} from './EnergyConsumption.sx';

dayjs.extend(utc);

// preset values from TimeMapEnum in initialData.ts
const computeDatesFromPreset = (preset: string | null): {from: number; to: number} => {
    const to = dayjs.utc().valueOf();
    switch (preset) {
        case '1': return {from: dayjs.utc().subtract(1, 'week').valueOf(), to};
        case '2': return {from: dayjs.utc().subtract(1, 'month').valueOf(), to};
        case '3': return {from: dayjs.utc().startOf('year').valueOf(), to};
        case '4': return {from: dayjs.utc('2020-01-01').valueOf(), to};
        default: return {from: dayjs.utc().startOf('year').valueOf(), to};
    }
};

export const EnergyConsumptionReport: FC = () => {
    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    const persona = usePersona();
    const isSuperAdmin = useCan('reports.readPlatform');
    const isCompanyUser = persona.isCompany;
    const [searchParams] = useSearchParams();
    const initialDates = useMemo(() => computeDatesFromPreset(searchParams.get('preset')), [searchParams]);

    const [dateFrom, setDateFrom] = useState<number>(initialDates.from);
    const [dateTo, setDateTo] = useState<number>(initialDates.to);
    const [energyType, setEnergyType] = useState<EnergyTypeFilter>(undefined);
    const [granularity, setGranularity] = useState<Granularity>('DAILY');

    const scopeFilters = useMemo<Partial<ConsumptionRequest>>(() => {
        // Nothing derived from the account: report-service reads the caller's reach from the token
        // and ANDs it into every aggregation, so a driver gets their own consumption and a company
        // gets its own whether or not the client says so.
        //
        // The business is the exception, and the only one. A caller who reaches every tenant is
        // clamped by nothing, so without this the figures were the whole platform's and did not move
        // when the business did. For everyone else it is the business they are already confined to,
        // so the filter changes nothing for them.
        return {businessId};
    }, [businessId]);

    const baseRequest: ConsumptionRequest = useMemo(() => ({
        dateFrom,
        dateTo,
        ...(energyType ? {energyType} : {}),
        ...scopeFilters,
    }), [dateFrom, dateTo, energyType, scopeFilters]);

    const {data: totals, isFetching: totalsLoading} = reportServiceApi.useGetConsumptionQuery(baseRequest);
    const {data: chartData, isFetching: chartLoading} = reportServiceApi.useGetConsumptionChartQuery({
        ...baseRequest,
        granularity,
    });

    const availableTabs: BreakdownTabKey[] = useMemo(() => {
        const tabs: BreakdownTabKey[] = ['locations', 'cities', 'chargers', 'realEstates'];
        if (!isCompanyUser) {
            tabs.push('users');
            tabs.push('companies');
        }
        if (isSuperAdmin) {
            tabs.push('businesses');
        }
        return tabs;
    }, [isCompanyUser, isSuperAdmin]);

    const handleDateChange = (from: number, to: number) => {
        setDateFrom(from);
        setDateTo(to);
    };

    return (
        <Box sx={energyConsumptionSx.wrapper}>
            <FiltersBar
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateChange={handleDateChange}
                energyType={energyType}
                onEnergyTypeChange={setEnergyType}
            />

            <KpiGrid data={totals} loading={totalsLoading} />

            <TimeSeriesChart
                data={chartData}
                loading={chartLoading}
                granularity={granularity}
                onGranularityChange={setGranularity}
            />

            <BreakdownTabs
                baseRequest={baseRequest}
                availableTabs={availableTabs}
            />
        </Box>
    );
};
